import { ReactElement, useContext } from 'react';

import { IconPersonAssist } from '@veneer/core';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './virtual-agent.module.scss';
import VirtualAgentContext from './virtualAgentContext';
import VirtualAgentChatButtonComponent from '../virtual-agent-chat-button/virtual-agent-chat-button';

export interface VirtualAgentProps {
  handleOnClickAction?: () => unknown;
  description?: ReactElement;
}

const VirtualAgentComponent = (props: Readonly<VirtualAgentProps>) => {
  const { handleOnClickAction, description } = props;
  const { t } = useTranslation();
  const { chatEnabled, appearance, chatWithUs, defaultColor } =
    useContext(VirtualAgentContext) ?? {};

  return appearance === 'button' ? (
    <VirtualAgentChatButtonComponent
      handleOnClickAction={handleOnClickAction}
    />
  ) : (
    <button
      className={classNames(styles['container'], 'virtual-agent')}
      onClick={!chatEnabled ? undefined : handleOnClickAction}
      data-testid="button-virtual-agent"
      id="button-virtual-agent"
    >
      <div
        className={classNames(
          styles['container-text'],
          styles['container-text-hidden']
        )}
        id="virtual-agent-text"
      >
        {chatWithUs && (
          <>
            <p>
              <b>{t('virtualAgent.needHelp')}</b>
            </p>
            <p>{t('virtualAgent.callUs')}</p>
            <p>{t('virtualAgent.orChatWithUs')}</p>
          </>
        )}
      </div>

      <div
        className={classNames(styles['container-icon'], {
          [styles['container-disabled']]: !chatEnabled,
          [styles['container-dark-icon']]: !chatWithUs,
          [styles['container-transparent-icon']]: appearance === 'transparent',
        })}
        id="button-virtual-agent-icon"
      >
        <IconPersonAssist
          aria-label={t('virtualAgent.icon')}
          className={styles['icon-chat']}
          color={defaultColor ? 'gray10' : 'white'}
          size={25}
          tabIndex={0}
          disabled={!chatEnabled}
          filled={appearance === 'transparent'}
        />
      </div>
      {description}
    </button>
  );
};

export function VirtualAgent(props: Readonly<VirtualAgentProps>) {
  const { handleOnClickAction, description } = props;

  return (
    <div id="virtual-agent-container">
      <VirtualAgentComponent
        handleOnClickAction={handleOnClickAction}
        description={description}
      />
    </div>
  );
}

export default VirtualAgent;
